<template>
  <article class="news-card" :class="{ 'news-card--pinned': item.pinned, 'news-card--draft': !item.enable }">
    <header class="news-card__header">
      <div class="news-card__titles">
        <div class="news-card__chips">
          <v-chip v-if="!item.enable" x-small color="grey darken-1" dark>Borrador</v-chip>
          <v-chip v-if="item.pinned" x-small color="primary" dark>Fijada</v-chip>
          <v-chip v-if="item.is_new" x-small color="orange" dark>Nueva</v-chip>
        </div>
        <h2 class="news-card__title">{{ item.title }}</h2>
        <div class="news-card__meta">{{ meta }}</div>
      </div>
      <div v-if="can_update || can_delete" class="news-card__actions">
        <v-btn v-if="can_update" icon small aria-label="Editar" title="Editar" @click="$emit('edit', item)">
          <v-icon small color="blue">mdi-pencil-outline</v-icon>
        </v-btn>
        <v-btn v-if="can_delete" icon small aria-label="Eliminar" title="Eliminar" @click="$emit('remove', item)">
          <v-icon small color="red">mdi-delete-outline</v-icon>
        </v-btn>
      </div>
    </header>

    <p v-if="item.body" class="news-card__body">{{ item.body }}</p>

    <div v-if="youtube_url" class="news-card__video">
      <iframe :src="youtube_url" :title="item.title" frameborder="0" allow="encrypted-media; picture-in-picture" allowfullscreen></iframe>
    </div>
    <a v-else-if="item.link_url" class="news-card__link" :href="item.link_url" target="_blank" rel="noopener noreferrer">
      <v-icon small color="primary">mdi-link-variant</v-icon>
      <span>{{ item.link_text || item.link_url }}</span>
    </a>

    <div v-if="item.attachments.length > 0" class="news-card__files">
      <v-carousel v-model="slide" :height="carousel_height" :show-arrows="many_files" :continuous="many_files" hide-delimiters>
        <v-carousel-item v-for="(attachment, index) in item.attachments" :key="attachment.id">
          <NewsAttachment :attachment="attachment" :active="slide == index" :height="carousel_height" />
        </v-carousel-item>
      </v-carousel>
      <span v-if="many_files" class="news-card__counter">{{ slide + 1 }} / {{ item.attachments.length }}</span>
    </div>

    <footer v-if="item.read_count != undefined && item.enable" class="news-card__footer">
      <v-icon x-small>mdi-eye-outline</v-icon>
      {{ item.read_count == 1 ? "1 lectura" : item.read_count + " lecturas" }}
    </footer>
  </article>
</template>

<script>
import NewsAttachment from "./NewsAttachment.vue";
import { formatDateTime, getYoutubeId } from "./newsModel";

export default {
  name: "NewsCard",
  components: { NewsAttachment },
  props: {
    item: Object,
    can_update: Boolean,
    can_delete: Boolean,
  },
  data() {
    return {
      slide: 0,
    };
  },
  computed: {
    many_files() {
      return this.item.attachments.length > 1;
    },
    carousel_height() {
      return this.$vuetify.breakpoint.xsOnly ? 300 : 420;
    },
    meta() {
      const date = formatDateTime(this.item.publication_date);
      if (!this.item.author) return date;
      return date ? this.item.author + " · " + date : this.item.author;
    },
    youtube_url() {
      const id = getYoutubeId(this.item.link_url);
      return id ? "https://www.youtube-nocookie.com/embed/" + id : null;
    },
  },
  watch: {
    // Al editar la novedad pueden quedar menos archivos que la posición actual
    "item.attachments"() {
      this.slide = 0;
    },
  },
};
</script>

<style scoped>
.news-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.news-card--pinned {
  border-left: 4px solid #28536b;
}
.news-card--draft {
  border-style: dashed;
  background: #f8fafc;
}
.news-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.news-card__titles {
  min-width: 0;
}
.news-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.news-card__chips .v-chip {
  margin-bottom: 6px;
}
.news-card__title {
  margin: 0;
  font-family: "Artificial Intelligence", "Manrope", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
  overflow-wrap: anywhere;
}
.news-card__meta {
  margin-top: 2px;
  font-size: 13px;
  color: #64748b;
}
.news-card__actions {
  display: flex;
  flex-shrink: 0;
}
.news-card__body {
  margin: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #334155;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.news-card__video {
  position: relative;
  padding-top: 56.25%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}
.news-card__video iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.news-card__link {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  text-decoration: none;
  overflow-wrap: anywhere;
}
.news-card__link span {
  min-width: 0;
}
.news-card__link:hover span {
  text-decoration: underline;
}
.news-card__files {
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.news-card__counter {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
}
.news-card__footer {
  font-size: 12px;
  color: #64748b;
}
</style>
